import React, { ReactNode, useMemo } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { skipToken } from '@reduxjs/toolkit/query'
import { isToday } from 'date-fns'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  ProviderSelfHarmTaskArguments,
  ProviderTaskType,
  ProviderTriageRiskTaskArguments,
  RiskSource,
} from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { SafetyPlan, SafetyPlanStatus } from '@lyrahealth-inc/shared-app-logic/src/models/safetyPlan/SafetyPlan'
import { SafetyPlanIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SafetyPlanIcon'
import { RiskAlertCard } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/riskAlertCard/RiskAlertCard'
import { RiskSummaryCard } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/riskSummaryCard/RiskSummaryCard'

import { CLIENT_RISK_SUMMARY } from '../../../../../common/constants/routingConstants'
import { parseTaskDate } from '../../../../../dashboard/openItems/utils'
import { getAuthUserId } from '../../../../../data/auth/authSelectors'
import { useGetClientEpisodesQuery, useGetRiskFlagQuery } from '../../../../episodes/data/episodesApi'
import { getSafetyPlanRoute, useClientSafetyPlan } from '../../../../safetyPlan/data/useClientSafetyPlan'
import useClientRiskStatus, { ClientRiskEntry } from '../../../clientList/v3/hooks/useClientRiskStatus'

type UseRiskOverviewArgs = {
  clientId?: string | null
  clientFirstName?: string
}

const getRiskSourceLabel = ({ task }: ClientRiskEntry, intl: IntlShape): string => {
  if (task.type === ProviderTaskType.TRIAGE_RISK) {
    return intl.formatMessage({
      defaultMessage: 'Search for care',
      description: 'Risk source: search for care triage',
    })
  }
  switch ((task.arguments as ProviderSelfHarmTaskArguments | undefined)?.risk_source) {
    case RiskSource.ONBOARDING:
      return intl.formatMessage({
        defaultMessage: 'Intake',
        description: 'Risk source: client intake',
      })
    case RiskSource.LYRA_AI:
      return intl.formatMessage({
        defaultMessage: 'Lyra AI',
        description: 'Risk source: Lyra AI conversation',
      })
    default:
      return intl.formatMessage({
        defaultMessage: 'Assessment',
        description: 'Risk source: assessment',
      })
  }
}

const getRiskEventDate = ({ task }: ClientRiskEntry): string | null => {
  if (task.type === ProviderTaskType.SELF_HARM) {
    const args = task.arguments as ProviderSelfHarmTaskArguments | undefined
    return parseTaskDate(args?.self_harm_date, args?.self_harm_time)
  }
  const args = task.arguments as ProviderTriageRiskTaskArguments | undefined
  return parseTaskDate(args?.event_date, args?.event_time)
}

const getRiskTimestamp = (riskEntry: ClientRiskEntry, intl: IntlShape): string | undefined => {
  const eventDate = getRiskEventDate(riskEntry)
  if (!eventDate) return undefined

  const date = new Date(eventDate)
  const dateLabel = isToday(date)
    ? intl.formatMessage({
        defaultMessage: 'Today',
        description: 'Label for a risk event that happened today',
      })
    : intl.formatDate(date, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
  const timeLabel = intl.formatTime(date, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })
  return intl.formatMessage(
    {
      defaultMessage: '{date} • {time}',
      description: 'Date and time of a client risk event',
    },
    { date: dateLabel, time: timeLabel },
  )
}

const getSafetyPlanSummary = (safetyPlan: SafetyPlan | undefined, intl: IntlShape) => {
  if (!safetyPlan) return undefined
  const isDraft = safetyPlan.status === SafetyPlanStatus.DRAFT
  const date = isDraft ? safetyPlan.update_date : safetyPlan.create_date
  return { createdDate: intl.formatDate(new Date(date), { month: 'short', day: 'numeric', year: 'numeric' }), isDraft }
}

export const useRiskOverview = ({
  clientId,
  clientFirstName,
}: UseRiskOverviewArgs): { riskAlert?: ReactNode; riskSummary?: ReactNode } => {
  const intl = useIntl()
  const navigate = useNavigate()
  const { isSafetyPlanEnabled } = useFlags()
  const riskStatuses = useClientRiskStatus()
  const riskEntry = isSafetyPlanEnabled && clientId ? riskStatuses.get(clientId) : undefined
  const isAddressed = riskEntry?.status === 'addressed_visible'

  const providerId = useSelector(getAuthUserId)
  const { data: episodes } = useGetClientEpisodesQuery(
    isAddressed && clientId ? { patientId: clientId, providerId } : skipToken,
  )
  const episodeId = episodes?.[0]?.id
  const { data: riskFlag } = useGetRiskFlagQuery(episodeId ? { episodeId } : skipToken)
  const { data: safetyPlan } = useClientSafetyPlan(riskEntry ? clientId : null)

  return useMemo(() => {
    if (!riskEntry || (isAddressed && !riskFlag)) {
      return {}
    }

    const goToRiskSummary = () => navigate(CLIENT_RISK_SUMMARY.route)
    const goToSafetyPlan = () => navigate(getSafetyPlanRoute(safetyPlan))

    const riskAlert = isAddressed ? undefined : (
      <RiskAlertCard
        headline={intl.formatMessage(
          {
            defaultMessage: '{clientFirstName} has indicated suicidal ideation',
            description: 'Headline of the risk alert shown on the client overview',
          },
          { clientFirstName: clientFirstName || 'This client' },
        )}
        // TODO: Show the AI generated risk narrative once the API is available.
        recommendedActions={[
          {
            id: 'reviewRiskSummary',
            title: intl.formatMessage({
              defaultMessage: 'Review risk summary for next steps',
              description: 'Recommended action to review the client risk summary',
            }),
            subtitle: intl.formatMessage({
              defaultMessage: 'This action must be addressed',
              description: 'Subtitle for a required recommended action',
            }),
            onPress: goToRiskSummary,
          },
          ...(safetyPlan
            ? []
            : [
                {
                  id: 'createSafetyPlan',
                  title: clientFirstName
                    ? intl.formatMessage(
                        {
                          defaultMessage: 'Create a safety plan with {clientFirstName}',
                          description: 'Recommended action to create a safety plan with the client',
                        },
                        { clientFirstName },
                      )
                    : intl.formatMessage({
                        defaultMessage: 'Create a safety plan',
                        description: 'Recommended action to create a safety plan when the client name is unknown',
                      }),
                  Icon: SafetyPlanIcon,
                  onPress: goToSafetyPlan,
                },
              ]),
        ]}
      />
    )

    const riskSummary = (
      <RiskSummaryCard
        risks={[
          {
            id: riskEntry.task.id,
            title: intl.formatMessage({
              defaultMessage: 'Suicidal ideation and intent',
              description: 'Risk summary row title for a suicidal ideation risk',
            }),
            source: getRiskSourceLabel(riskEntry, intl),
            timestamp: getRiskTimestamp(riskEntry, intl),
            requiresAttention: !isAddressed,
          },
        ]}
        safetyPlan={getSafetyPlanSummary(safetyPlan, intl)}
        onSafetyPlanPress={goToSafetyPlan}
        onViewAllPress={goToRiskSummary}
      />
    )

    return { riskAlert, riskSummary }
  }, [riskEntry, isAddressed, riskFlag, safetyPlan, clientFirstName, intl, navigate])
}
